import React, { useState, useEffect } from 'react'
import {
    Box,
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import { CloudUpload, MoreVert, Search, FindInPage } from '@material-ui/icons'

import {
    DocumentProcessingStatusFilter,
    LeaseAuditStep,
    LeaseDocument,
} from '../../types'
import { useLeaseAuditDetail } from '../../hooks/useLeaseAuditDetail'
import { useDocuments } from '../../hooks'
import { DocumentsTableView } from './DocumentsTableView'
import { DocumentUploadZone } from './DocumentUploadZone'
import { DocumentParsingProgress } from './DocumentParsingProgress'
import { DocumentExtractionProgress } from './DocumentExtractionProgress'
import { useLeaseDocumentsRootStyles } from './styles'
import { HEADER_HEIGHT, COLLAPSABLE_SELECTOR_HEIGHT } from '../constants'

interface LeaseDocumentsTabProps {
    auditId: number
    currentStep: LeaseAuditStep
    parsingTaskId?: string | null
    extractionTaskId?: string | null
    isCollapsableSelectorOpen: boolean
    showUploadZone?: boolean
    onOpenUploadZone?: () => void
    onCloseUploadZone?: () => void
}

export const LeaseDocumentsRoot: React.FC<LeaseDocumentsTabProps> = ({
    auditId,
    currentStep,
    parsingTaskId,
    extractionTaskId,
    isCollapsableSelectorOpen,
    showUploadZone = false,
    onOpenUploadZone,
    onCloseUploadZone,
}) => {
    const headerHeight = HEADER_HEIGHT
    const collapsableSelectorHeight = isCollapsableSelectorOpen
        ? COLLAPSABLE_SELECTOR_HEIGHT
        : 0
    const classes = useLeaseDocumentsRootStyles({
        headerHeight,
        collapsableSelectorHeight,
    })
    const { leaseAudit } = useLeaseAuditDetail({ auditId })
    const [documentSearch, setDocumentSearch] = useState('')
    const [documentPage, setDocumentPage] = useState(1)
    const [documentPageSize, setDocumentPageSize] = useState(25)
    const [
        documentProcessingStatusFilter,
        setDocumentProcessingStatusFilter,
    ] = useState<DocumentProcessingStatusFilter | undefined>(undefined)
    const {
        documents,
        totalCount: documentsTotalCount,
        isLoading: isLoadingDocuments,
        parsingTaskProgress,
        extractionTaskProgress,
        beginParse,
        isLoadingParsingTask,
        isLoadingExtractionTask,
        parsingTaskError,
        extractionTaskError,
        parsingTaskFailureMessage,
        parsingTaskHasFailed,
        extractionTaskFailureMessage,
        extractionTaskHasFailed,
        extractDocuments,
    } = useDocuments({
        auditId,
        page: documentPage,
        pageSize: documentPageSize,
        search: documentSearch,
        enabled: true,
        processingStatus: documentProcessingStatusFilter,
        parsingTaskId,
        extractionTaskId,
    })

    const actionMenuSecondaryTypographyProps = {
        style: {
            whiteSpace: 'normal' as const,
            maxWidth: 260,
        },
    }

    const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([])
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
        null,
    )

    React.useEffect(() => {
        if (documents.length === 0 && selectedDocumentIds.length === 0) {
            return
        }
        const next = selectedDocumentIds.filter((id) =>
            documents.some((doc: LeaseDocument) => doc.id === id),
        )
        const isSame =
            selectedDocumentIds.length === next.length &&
            selectedDocumentIds.every((value, index) => value === next[index])
        if (!isSame) {
            setSelectedDocumentIds(next)
        }
    }, [documents, selectedDocumentIds])

    const handleActionsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setActionsAnchorEl(event.currentTarget)
    }

    const handleActionsClose = () => {
        setActionsAnchorEl(null)
    }

    useEffect(() => {
        setDocumentPage(1)
    }, [documentSearch, documentProcessingStatusFilter, documentPageSize])

    const canExtractDocuments =
        leaseAudit?.current_step === 'DATA_EXTRACTION' ||
        leaseAudit?.current_step === 'AUDITING'

    const isFirstTimeUploadStep =
        currentStep === 'DOCUMENT_UPLOAD' &&
        leaseAudit?.audit_type === 'UPLOADED'

    const shouldShowUploadZone =
        (showUploadZone || isFirstTimeUploadStep) &&
        !parsingTaskId &&
        !extractionTaskId

    const handleUploadComplete = () => {
        onCloseUploadZone?.()
    }

    if (
        currentStep === 'DOCUMENT_UPLOAD' &&
        leaseAudit?.audit_type === 'INTEGRATED'
    ) {
        return (
            <Box className={classes.container}>
                <Box className={classes.emptyState}>
                    <Box className={classes.emptyStateCard}>
                        <FindInPage className={classes.emptyStateIcon} />
                        <Box>
                            <Typography variant="h6" align="center">
                                Lease documents not loaded
                            </Typography>
                            <Typography variant="body2" align="center">
                                Pull the latest lease documents to begin parsing
                                and continue the audit.
                            </Typography>
                        </Box>
                        <Box mt={1}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => beginParse()}
                                disabled={isLoadingParsingTask}
                            >
                                {isLoadingParsingTask
                                    ? 'Getting Lease Documents'
                                    : 'Get Lease Documents'}
                            </Button>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                            This may take a minute.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        )
    }

    if (parsingTaskId) {
        return (
            <Box>
                <DocumentParsingProgress
                    taskProgress={parsingTaskProgress}
                    isLoading={isLoadingParsingTask}
                    error={parsingTaskError}
                    hasFailed={parsingTaskHasFailed}
                    failureMessage={parsingTaskFailureMessage}
                />
            </Box>
        )
    }

    if (extractionTaskId) {
        return (
            <Box>
                <DocumentExtractionProgress
                    taskProgress={extractionTaskProgress}
                    isLoading={isLoadingExtractionTask}
                    error={extractionTaskError}
                    hasFailed={extractionTaskHasFailed}
                    failureMessage={extractionTaskFailureMessage}
                />
            </Box>
        )
    }

    if (shouldShowUploadZone) {
        return (
            <DocumentUploadZone
                auditId={auditId}
                onUploadComplete={handleUploadComplete}
                showAdditiveUploadNote={
                    showUploadZone && !isFirstTimeUploadStep
                }
            />
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.headerRow}>
                <Box className={classes.headerControls}>
                    <TextField
                        style={{ minWidth: 350 }}
                        value={documentSearch}
                        onChange={(event) => {
                            setDocumentSearch(event.target.value)
                        }}
                        placeholder="Search Document Title or Resident Name"
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        label="Filter"
                        value={documentProcessingStatusFilter}
                        onChange={(
                            event: React.ChangeEvent<{ value: unknown }>,
                        ) => {
                            const nextValue = event.target.value as
                                | DocumentProcessingStatusFilter
                                | ''
                            setDocumentProcessingStatusFilter(
                                nextValue === '' ? undefined : nextValue,
                            )
                        }}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 180 }}
                    >
                        <MenuItem value="SELECTION_COMPLETE">
                            Selected for Audit
                        </MenuItem>
                        <MenuItem value="EXTRACTION_FAILED">
                            Failed to Extract
                        </MenuItem>
                    </TextField>
                </Box>
                <Box className={classes.headerControls}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MoreVert />}
                        onClick={handleActionsOpen}
                        className={classes.actionButton}
                    >
                        Actions
                    </Button>
                    <Menu
                        anchorEl={actionsAnchorEl}
                        open={Boolean(actionsAnchorEl)}
                        onClose={handleActionsClose}
                    >
                        <MenuItem
                            disabled={
                                selectedDocumentIds.length === 0 ||
                                !canExtractDocuments
                            }
                            onClick={() => {
                                extractDocuments({
                                    documentIds: selectedDocumentIds,
                                })
                                handleActionsClose()
                            }}
                        >
                            <ListItemIcon>
                                <FindInPage fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Extract Selected Documents"
                                secondary="Extract attribute data from the selected documents."
                                secondaryTypographyProps={
                                    actionMenuSecondaryTypographyProps
                                }
                            />
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                onOpenUploadZone?.()
                                handleActionsClose()
                            }}
                            disabled={leaseAudit?.audit_type !== 'UPLOADED'}
                        >
                            <ListItemIcon>
                                <CloudUpload fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Upload Documents"
                                secondary="Upload more documents. Parsing and selection will rerun; previously selected documents may change."
                                secondaryTypographyProps={
                                    actionMenuSecondaryTypographyProps
                                }
                            />
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <DocumentsTableView
                documents={documents}
                totalCount={documentsTotalCount}
                page={documentPage}
                pageSize={documentPageSize}
                isLoading={isLoadingDocuments}
                onPageChange={setDocumentPage}
                onPageSizeChange={setDocumentPageSize}
                selectedDocumentIds={selectedDocumentIds}
                onSelectionChange={setSelectedDocumentIds}
            />
        </Box>
    )
}
